import {
  z,
  createUploadedFilesSchema,
  createFileUploadValidator,
} from '@tasama/utils';

export const getActivityRequestSchema = (t: (_key: string) => string) =>
  z.object({
    title: z
      .string()
      .min(1, t('services.activityRequest.validation.titleRequired')),
    activitySysId: z
      .string()
      .min(1, t('services.activityRequest.validation.activityRequired')),
  });

export const activityRequestSchema = getActivityRequestSchema((key) => key);

/**
 * The one Activity Request attachment contract, per TDF-1056 §4 "Supporting Document
 * Constraints" (BRD §4.3.2) and the Figma annotation on the Activity Request form:
 * PDF, PNG, CSV, Excel, Word, JPG, 5 MB per file, 10 files per request.
 *
 * Every Activity Request upload surface reads these, so the accept filter, the drop-zone
 * hint and the validation messages cannot drift apart again: the create form's supporting
 * documents, each dynamic 'file' field, and the post-submission reply composer.
 */
export const ACTIVITY_REQUEST_ALLOWED_EXTS = [
  'pdf',
  'png',
  'jpg',
  'jpeg',
  'csv',
  'xlsx',
  'xls',
  'docx',
  'doc',
] as const;

export const ACTIVITY_REQUEST_ACCEPT = ACTIVITY_REQUEST_ALLOWED_EXTS.map(
  (ext) => `.${ext}`
).join(',');

export const ACTIVITY_REQUEST_MAX_FILE_BYTES = 5 * 1024 * 1024;

export const ACTIVITY_REQUEST_MAX_FILES = 10;

// Used by the dynamic-form file fields and by ActivityRequestViewPanel's reply/response
// composer (attachment uploads on an already-submitted request). The create-form fields
// above are unrelated.
const getActivityRequestFilesSchema = (t: (_key: string) => string) =>
  z.object({
    uploadedFiles: createUploadedFilesSchema({
      maxBytes: ACTIVITY_REQUEST_MAX_FILE_BYTES,
      allowedExts: [...ACTIVITY_REQUEST_ALLOWED_EXTS],
      messages: {
        tooLarge: t('services.activityRequest.validation.tooLarge'),
        invalidType: t('services.activityRequest.validation.invalidType'),
      },
    }),
  });

export const getValidateActivityRequestFiles = (t: (_key: string) => string) =>
  createFileUploadValidator(getActivityRequestFilesSchema(t));

export type ActivityRequestFormValues = z.infer<typeof activityRequestSchema>;
